import React, { useState, useEffect } from 'react';
import { Star, ArrowLeft } from 'react-iconly';
import { getProductReviews } from '../../../lib/reviewsService';

// Style guide colors
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const PRIMARY_BROWN = '#f4a261';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

export function ProductReviewsDrawer({ 
  isOpen, 
  onClose, 
  product, 
  productRating = 0, 
  productReviewCount = 0 
}) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ratingBreakdown, setRatingBreakdown] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load reviews when drawer opens
  useEffect(() => {
    if (isOpen && product?.id) {
      loadReviews();
    }
  }, [isOpen, product?.id]);

  const loadReviews = async (offset = 0, limit = 20) => {
    if (!product?.id) return;

    try {
      if (offset === 0) {
        setLoading(true);
        setError('');
      } else {
        setLoadingMore(true);
      }

      const reviewsData = await getProductReviews(product.id, limit, offset);
      
      if (offset === 0) {
        setReviews(reviewsData);
        calculateRatingBreakdown(reviewsData);
      } else {
        setReviews(prev => [...prev, ...reviewsData]);
      }

      setHasMore(reviewsData.length === limit);
    } catch (err) {
      console.error('Error loading reviews:', err);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const calculateRatingBreakdown = (reviewsData) => {
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviewsData.forEach(review => {
      breakdown[review.rating]++;
    });

    const total = reviewsData.length;
    const percentageBreakdown = {};
    Object.keys(breakdown).forEach(rating => {
      percentageBreakdown[rating] = total > 0 ? (breakdown[rating] / total) * 100 : 0;
    });

    setRatingBreakdown(percentageBreakdown);
  };

  const loadMoreReviews = () => {
    if (!loadingMore && hasMore) {
      loadReviews(reviews.length);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        set={i < rating ? 'bold' : 'light'}
        primaryColor={i < rating ? '#f4a261' : '#E0E0E0'}
        size={16}
      />
    ));
  };

  const renderRatingBreakdown = () => {
    const totalReviews = reviews.length;
    if (totalReviews === 0) return null;

    return (
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ ...headingFont, fontSize: 16, marginBottom: 12, color: NEUTRAL_TEXT }}>
          Rating Breakdown
        </h3>
        {[5, 4, 3, 2, 1].map(rating => (
          <div key={rating} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: 8 
          }}>
            <span style={{ 
              ...bodyFont, 
              fontSize: 14, 
              color: NEUTRAL_TEXT, 
              minWidth: 20 
            }}>
              {rating}★
            </span>
            <div style={{ 
              flex: 1, 
              height: 8, 
              backgroundColor: '#E0E0E0', 
              borderRadius: 4, 
              margin: '0 12px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${ratingBreakdown[rating] || 0}%`,
                height: '100%',
                backgroundColor: '#f4a261',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <span style={{ 
              ...bodyFont, 
              fontSize: 12, 
              color: ACCENT_GRAY, 
              minWidth: 40,
              textAlign: 'right'
            }}>
              {Math.round(ratingBreakdown[rating] || 0)}%
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderEmptyState = () => (
    <div style={{
      textAlign: 'center',
      padding: '40px 20px',
      color: ACCENT_GRAY
    }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⭐</div>
      <h3 style={{ ...headingFont, fontSize: 18, marginBottom: 8, color: NEUTRAL_TEXT }}>
        No reviews yet
      </h3>
      <p style={{ ...bodyFont, fontSize: 14 }}>
        Be the first to try this product and leave a review!
      </p>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          width: '100%',
          maxHeight: '85vh',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: '20px',
          overflowY: 'auto',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          paddingBottom: 15,
          borderBottom: '1px solid #eee'
        }}>
          <div>
            <h2 style={{ ...headingFont, fontSize: 20, margin: 0, color: NEUTRAL_TEXT }}>
              {product?.name || 'Product Reviews'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {renderStars(Math.round(productRating))}
              </div>
              <span style={{ ...bodyFont, fontSize: 14, color: ACCENT_GRAY }}>
                {productRating.toFixed(1)} ({productReviewCount} reviews)
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#666',
              padding: 4
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ ...bodyFont, fontSize: 16, color: ACCENT_GRAY }}>
              Loading reviews...
            </div>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ ...bodyFont, fontSize: 16, color: '#F44336' }}>
              {error}
            </div>
          </div>
        ) : reviews.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            {/* Rating Breakdown */}
            {renderRatingBreakdown()}

            {/* Reviews List */}
            <div>
              <h3 style={{ ...headingFont, fontSize: 16, marginBottom: 16, color: NEUTRAL_TEXT }}>
                Customer Reviews
              </h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {reviews.map((review, index) => (
                  <div key={review.id || index} style={{
                    padding: '16px 0',
                    borderBottom: index < reviews.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ ...bodyFont, fontSize: 14, fontWeight: 600, color: NEUTRAL_TEXT }}>
                          {review.customerName || 'Anonymous'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                          {renderStars(review.rating)}
                        </div>
                      </div>
                      <div style={{ ...bodyFont, fontSize: 12, color: ACCENT_GRAY }}>
                        {formatDate(review.createdAt?.toDate ? review.createdAt.toDate() : review.createdAt)}
                      </div>
                    </div>
                    {review.comment && (
                      <div style={{ 
                        ...bodyFont, 
                        fontSize: 14, 
                        color: NEUTRAL_TEXT, 
                        lineHeight: 1.5,
                        marginTop: 8
                      }}>
                        {review.comment}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                  <button
                    onClick={loadMoreReviews}
                    disabled={loadingMore}
                    style={{
                      background: 'transparent',
                      border: `2px solid ${PRIMARY_BROWN}`,
                      borderRadius: 8,
                      padding: '8px 24px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: loadingMore ? 'not-allowed' : 'pointer',
                      color: PRIMARY_BROWN,
                      ...bodyFont,
                      opacity: loadingMore ? 0.6 : 1
                    }}
                  >
                    {loadingMore ? 'Loading...' : 'Load More Reviews'}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ProductReviewsDrawer;
